export interface TechnologySymbolMeta {
  symbol: string;
  label: string;
  className: string;
  accent: string;
  /** simple-icons slug for https://cdn.simpleicons.org/{slug} */
  iconSlug?: string;
  /** Tailwind classes for text fallback badge */
  fallbackClass: string;
}

/** Maps detected technology labels → Simple Icons slugs */
const TECHNOLOGY_MAP: Record<string, TechnologySymbolMeta> = {
  // Languages
  JavaScript: {
    symbol: 'JS',
    label: 'JavaScript',
    className: 'tech-symbol--javascript',
    accent: 'javascript',
    iconSlug: 'javascript',
    fallbackClass: 'bg-[#f7df1e] text-[#323330]',
  },
  TypeScript: {
    symbol: 'TS',
    label: 'TypeScript',
    className: 'tech-symbol--typescript',
    accent: 'typescript',
    iconSlug: 'typescript',
    fallbackClass: 'bg-[#3178c6] text-white',
  },
  'C#': {
    symbol: 'C#',
    label: 'C#',
    className: 'tech-symbol--csharp',
    accent: 'csharp',
    iconSlug: 'csharp',
    fallbackClass: 'bg-[#512bd4] text-white',
  },
  Java: {
    symbol: 'Ja',
    label: 'Java',
    className: 'tech-symbol--other',
    accent: 'java',
    iconSlug: 'openjdk',
    fallbackClass: 'bg-[#ed8b00] text-white',
  },
  Kotlin: {
    symbol: 'Kt',
    label: 'Kotlin',
    className: 'tech-symbol--other',
    accent: 'kotlin',
    iconSlug: 'kotlin',
    fallbackClass: 'bg-[#7f52ff] text-white',
  },
  Python: {
    symbol: 'Py',
    label: 'Python',
    className: 'tech-symbol--other',
    accent: 'python',
    iconSlug: 'python',
    fallbackClass: 'bg-[#3776ab] text-white',
  },
  Go: {
    symbol: 'Go',
    label: 'Go',
    className: 'tech-symbol--other',
    accent: 'go',
    iconSlug: 'go',
    fallbackClass: 'bg-[#00add8] text-white',
  },
  Rust: {
    symbol: 'Rs',
    label: 'Rust',
    className: 'tech-symbol--other',
    accent: 'rust',
    iconSlug: 'rust',
    fallbackClass: 'bg-[#000000] text-white',
  },
  PHP: {
    symbol: 'PHP',
    label: 'PHP',
    className: 'tech-symbol--other',
    accent: 'php',
    iconSlug: 'php',
    fallbackClass: 'bg-[#777bb4] text-white',
  },
  Ruby: {
    symbol: 'Rb',
    label: 'Ruby',
    className: 'tech-symbol--other',
    accent: 'ruby',
    iconSlug: 'ruby',
    fallbackClass: 'bg-[#cc342d] text-white',
  },
  Scala: {
    symbol: 'Sc',
    label: 'Scala',
    className: 'tech-symbol--other',
    accent: 'scala',
    iconSlug: 'scala',
    fallbackClass: 'bg-[#dc322f] text-white',
  },
  Swift: {
    symbol: 'Sw',
    label: 'Swift',
    className: 'tech-symbol--other',
    accent: 'swift',
    iconSlug: 'swift',
    fallbackClass: 'bg-[#f05138] text-white',
  },
  SQL: {
    symbol: 'SQL',
    label: 'SQL',
    className: 'tech-symbol--other',
    accent: 'sql',
    iconSlug: 'postgresql',
    fallbackClass: 'bg-[#336791] text-white',
  },
  '.NET': {
    symbol: '.N',
    label: '.NET',
    className: 'tech-symbol--other',
    accent: 'dotnet',
    iconSlug: 'dotnet',
    fallbackClass: 'bg-[#512bd4] text-white',
  },

  // Package managers
  npm: {
    symbol: 'npm',
    label: 'npm',
    className: 'tech-symbol--other',
    accent: 'npm',
    iconSlug: 'npm',
    fallbackClass: 'bg-[#cb3837] text-white',
  },
  Yarn: {
    symbol: 'Ya',
    label: 'Yarn',
    className: 'tech-symbol--other',
    accent: 'yarn',
    iconSlug: 'yarn',
    fallbackClass: 'bg-[#2c8ebb] text-white',
  },
  pnpm: {
    symbol: 'pn',
    label: 'pnpm',
    className: 'tech-symbol--other',
    accent: 'pnpm',
    iconSlug: 'pnpm',
    fallbackClass: 'bg-[#f69220] text-white',
  },
  Bun: {
    symbol: 'Bn',
    label: 'Bun',
    className: 'tech-symbol--other',
    accent: 'bun',
    iconSlug: 'bun',
    fallbackClass: 'bg-[#000000] text-white',
  },
  Composer: {
    symbol: 'Cp',
    label: 'Composer',
    className: 'tech-symbol--other',
    accent: 'composer',
    iconSlug: 'composer',
    fallbackClass: 'bg-[#885630] text-white',
  },
  Poetry: {
    symbol: 'Po',
    label: 'Poetry',
    className: 'tech-symbol--other',
    accent: 'poetry',
    iconSlug: 'poetry',
    fallbackClass: 'bg-[#60a5fa] text-white',
  },
  Cargo: {
    symbol: 'Ca',
    label: 'Cargo',
    className: 'tech-symbol--other',
    accent: 'cargo',
    iconSlug: 'rust',
    fallbackClass: 'bg-[#dea584] text-[#1f1f1f]',
  },
  RubyGems: {
    symbol: 'Rg',
    label: 'RubyGems',
    className: 'tech-symbol--other',
    accent: 'rubygems',
    iconSlug: 'rubygems',
    fallbackClass: 'bg-[#e9573f] text-white',
  },
  pip: {
    symbol: 'pi',
    label: 'pip',
    className: 'tech-symbol--other',
    accent: 'pip',
    iconSlug: 'pypi',
    fallbackClass: 'bg-[#3775a9] text-white',
  },
  'Go modules': {
    symbol: 'Go',
    label: 'Go modules',
    className: 'tech-symbol--other',
    accent: 'go',
    iconSlug: 'go',
    fallbackClass: 'bg-[#00add8] text-white',
  },

  // Runtimes
  'Node.js': {
    symbol: 'Nd',
    label: 'Node.js',
    className: 'tech-symbol--other',
    accent: 'nodejs',
    iconSlug: 'nodedotjs',
    fallbackClass: 'bg-[#5fa04e] text-white',
  },

  // Frontend frameworks
  React: {
    symbol: 'Re',
    label: 'React',
    className: 'tech-symbol--other',
    accent: 'react',
    iconSlug: 'react',
    fallbackClass: 'bg-[#61dafb] text-[#20232a]',
  },
  'Next.js': {
    symbol: 'Nx',
    label: 'Next.js',
    className: 'tech-symbol--other',
    accent: 'nextjs',
    iconSlug: 'nextdotjs',
    fallbackClass: 'bg-[#000000] text-white',
  },
  Vue: {
    symbol: 'Vu',
    label: 'Vue',
    className: 'tech-symbol--other',
    accent: 'vue',
    iconSlug: 'vuedotjs',
    fallbackClass: 'bg-[#4fc08d] text-white',
  },
  Nuxt: {
    symbol: 'Nu',
    label: 'Nuxt',
    className: 'tech-symbol--other',
    accent: 'nuxt',
    iconSlug: 'nuxtdotjs',
    fallbackClass: 'bg-[#00dc82] text-[#181818]',
  },
  Angular: {
    symbol: 'Ng',
    label: 'Angular',
    className: 'tech-symbol--other',
    accent: 'angular',
    iconSlug: 'angular',
    fallbackClass: 'bg-[#dd0031] text-white',
  },
  Svelte: {
    symbol: 'Sv',
    label: 'Svelte',
    className: 'tech-symbol--other',
    accent: 'svelte',
    iconSlug: 'svelte',
    fallbackClass: 'bg-[#ff3e00] text-white',
  },
  SolidJS: {
    symbol: 'So',
    label: 'SolidJS',
    className: 'tech-symbol--other',
    accent: 'solidjs',
    iconSlug: 'solid',
    fallbackClass: 'bg-[#2c4f7c] text-white',
  },
  Astro: {
    symbol: 'As',
    label: 'Astro',
    className: 'tech-symbol--other',
    accent: 'astro',
    iconSlug: 'astro',
    fallbackClass: 'bg-[#bc52ee] text-white',
  },
  Remix: {
    symbol: 'Rm',
    label: 'Remix',
    className: 'tech-symbol--other',
    accent: 'remix',
    iconSlug: 'remix',
    fallbackClass: 'bg-[#121212] text-white',
  },
  Gatsby: {
    symbol: 'Ga',
    label: 'Gatsby',
    className: 'tech-symbol--other',
    accent: 'gatsby',
    iconSlug: 'gatsby',
    fallbackClass: 'bg-[#663399] text-white',
  },
  Qwik: {
    symbol: 'Qw',
    label: 'Qwik',
    className: 'tech-symbol--other',
    accent: 'qwik',
    iconSlug: 'qwik',
    fallbackClass: 'bg-[#18b6f6] text-white',
  },

  // Backend frameworks
  Express: {
    symbol: 'Ex',
    label: 'Express',
    className: 'tech-symbol--other',
    accent: 'express',
    iconSlug: 'express',
    fallbackClass: 'bg-[#000000] text-white',
  },
  NestJS: {
    symbol: 'Ne',
    label: 'NestJS',
    className: 'tech-symbol--other',
    accent: 'nestjs',
    iconSlug: 'nestjs',
    fallbackClass: 'bg-[#e0234e] text-white',
  },
  Fastify: {
    symbol: 'Fa',
    label: 'Fastify',
    className: 'tech-symbol--other',
    accent: 'fastify',
    iconSlug: 'fastify',
    fallbackClass: 'bg-[#000000] text-white',
  },
  Hono: {
    symbol: 'Ho',
    label: 'Hono',
    className: 'tech-symbol--other',
    accent: 'hono',
    iconSlug: 'hono',
    fallbackClass: 'bg-[#ff5b11] text-white',
  },
  Koa: {
    symbol: 'Ko',
    label: 'Koa',
    className: 'tech-symbol--other',
    accent: 'koa',
    iconSlug: 'koa',
    fallbackClass: 'bg-[#33333d] text-white',
  },
  'Spring Boot': {
    symbol: 'Sp',
    label: 'Spring Boot',
    className: 'tech-symbol--other',
    accent: 'spring',
    iconSlug: 'springboot',
    fallbackClass: 'bg-[#6db33f] text-white',
  },
  Flask: {
    symbol: 'Fl',
    label: 'Flask',
    className: 'tech-symbol--other',
    accent: 'flask',
    iconSlug: 'flask',
    fallbackClass: 'bg-[#000000] text-white',
  },
  Django: {
    symbol: 'Dj',
    label: 'Django',
    className: 'tech-symbol--other',
    accent: 'django',
    iconSlug: 'django',
    fallbackClass: 'bg-[#092e20] text-white',
  },
  FastAPI: {
    symbol: 'FA',
    label: 'FastAPI',
    className: 'tech-symbol--other',
    accent: 'fastapi',
    iconSlug: 'fastapi',
    fallbackClass: 'bg-[#009688] text-white',
  },
  Laravel: {
    symbol: 'La',
    label: 'Laravel',
    className: 'tech-symbol--other',
    accent: 'laravel',
    iconSlug: 'laravel',
    fallbackClass: 'bg-[#ff2d20] text-white',
  },
  'ASP.NET': {
    symbol: 'AS',
    label: 'ASP.NET',
    className: 'tech-symbol--other',
    accent: 'aspnet',
    iconSlug: 'dotnet',
    fallbackClass: 'bg-[#512bd4] text-white',
  },
  Gin: {
    symbol: 'Gi',
    label: 'Gin',
    className: 'tech-symbol--other',
    accent: 'gin',
    iconSlug: 'go',
    fallbackClass: 'bg-[#00add8] text-white',
  },
  Fiber: {
    symbol: 'Fi',
    label: 'Fiber',
    className: 'tech-symbol--other',
    accent: 'fiber',
    iconSlug: 'go',
    fallbackClass: 'bg-[#00add8] text-white',
  },
  Echo: {
    symbol: 'Ec',
    label: 'Echo',
    className: 'tech-symbol--other',
    accent: 'echo',
    iconSlug: 'go',
    fallbackClass: 'bg-[#00add8] text-white',
  },
  Actix: {
    symbol: 'Ac',
    label: 'Actix',
    className: 'tech-symbol--other',
    accent: 'actix',
    iconSlug: 'rust',
    fallbackClass: 'bg-[#000000] text-white',
  },
  Rocket: {
    symbol: 'Ro',
    label: 'Rocket',
    className: 'tech-symbol--other',
    accent: 'rocket',
    iconSlug: 'rust',
    fallbackClass: 'bg-[#d33847] text-white',
  },
  'Ruby on Rails': {
    symbol: 'Rl',
    label: 'Ruby on Rails',
    className: 'tech-symbol--other',
    accent: 'rails',
    iconSlug: 'rubyonrails',
    fallbackClass: 'bg-[#cc0000] text-white',
  },

  // UI libraries
  'Material UI': {
    symbol: 'MU',
    label: 'Material UI',
    className: 'tech-symbol--other',
    accent: 'mui',
    iconSlug: 'mui',
    fallbackClass: 'bg-[#007fff] text-white',
  },
  'Chakra UI': {
    symbol: 'Ch',
    label: 'Chakra UI',
    className: 'tech-symbol--other',
    accent: 'chakra',
    iconSlug: 'chakraui',
    fallbackClass: 'bg-[#319795] text-white',
  },
  'Ant Design': {
    symbol: 'An',
    label: 'Ant Design',
    className: 'tech-symbol--other',
    accent: 'antd',
    iconSlug: 'antdesign',
    fallbackClass: 'bg-[#0170fe] text-white',
  },
  Mantine: {
    symbol: 'Ma',
    label: 'Mantine',
    className: 'tech-symbol--other',
    accent: 'mantine',
    iconSlug: 'mantine',
    fallbackClass: 'bg-[#339af0] text-white',
  },
  'Radix UI': {
    symbol: 'Ra',
    label: 'Radix UI',
    className: 'tech-symbol--other',
    accent: 'radix',
    iconSlug: 'radixui',
    fallbackClass: 'bg-[#161618] text-white',
  },
  'Tailwind CSS': {
    symbol: 'Tw',
    label: 'Tailwind CSS',
    className: 'tech-symbol--other',
    accent: 'tailwind',
    iconSlug: 'tailwindcss',
    fallbackClass: 'bg-[#06b6d4] text-white',
  },
  Bootstrap: {
    symbol: 'Bs',
    label: 'Bootstrap',
    className: 'tech-symbol--other',
    accent: 'bootstrap',
    iconSlug: 'bootstrap',
    fallbackClass: 'bg-[#7952b3] text-white',
  },
  Bulma: {
    symbol: 'Bu',
    label: 'Bulma',
    className: 'tech-symbol--other',
    accent: 'bulma',
    iconSlug: 'bulma',
    fallbackClass: 'bg-[#00d1b2] text-white',
  },
  'Semantic UI': {
    symbol: 'Se',
    label: 'Semantic UI',
    className: 'tech-symbol--other',
    accent: 'semanticui',
    iconSlug: 'semanticui',
    fallbackClass: 'bg-[#35bdb2] text-white',
  },

  // State / routing
  Redux: {
    symbol: 'Rx',
    label: 'Redux',
    className: 'tech-symbol--other',
    accent: 'redux',
    iconSlug: 'redux',
    fallbackClass: 'bg-[#764abc] text-white',
  },
  'Redux Toolkit': {
    symbol: 'RT',
    label: 'Redux Toolkit',
    className: 'tech-symbol--other',
    accent: 'redux',
    iconSlug: 'redux',
    fallbackClass: 'bg-[#764abc] text-white',
  },
  MobX: {
    symbol: 'Mb',
    label: 'MobX',
    className: 'tech-symbol--other',
    accent: 'mobx',
    iconSlug: 'mobx',
    fallbackClass: 'bg-[#ff9955] text-white',
  },
  Zustand: {
    symbol: 'Zu',
    label: 'Zustand',
    className: 'tech-symbol--other',
    accent: 'zustand',
    iconSlug: 'zustand',
    fallbackClass: 'bg-[#443e38] text-white',
  },
  Jotai: {
    symbol: 'Jo',
    label: 'Jotai',
    className: 'tech-symbol--other',
    accent: 'jotai',
    iconSlug: 'jotai',
    fallbackClass: 'bg-[#000000] text-white',
  },
  Recoil: {
    symbol: 'Rc',
    label: 'Recoil',
    className: 'tech-symbol--other',
    accent: 'recoil',
    iconSlug: 'recoil',
    fallbackClass: 'bg-[#3578e5] text-white',
  },
  XState: {
    symbol: 'XS',
    label: 'XState',
    className: 'tech-symbol--other',
    accent: 'xstate',
    iconSlug: 'xstate',
    fallbackClass: 'bg-[#2c3e50] text-white',
  },
  'React Router': {
    symbol: 'RR',
    label: 'React Router',
    className: 'tech-symbol--other',
    accent: 'reactrouter',
    iconSlug: 'reactrouter',
    fallbackClass: 'bg-[#ca4245] text-white',
  },
  'TanStack Router': {
    symbol: 'TR',
    label: 'TanStack Router',
    className: 'tech-symbol--other',
    accent: 'tanstack',
    iconSlug: 'reactquery',
    fallbackClass: 'bg-[#ff4154] text-white',
  },

  // Build tools
  Vite: {
    symbol: 'Vi',
    label: 'Vite',
    className: 'tech-symbol--other',
    accent: 'vite',
    iconSlug: 'vite',
    fallbackClass: 'bg-[#646cff] text-white',
  },
  Webpack: {
    symbol: 'Wp',
    label: 'Webpack',
    className: 'tech-symbol--other',
    accent: 'webpack',
    iconSlug: 'webpack',
    fallbackClass: 'bg-[#8dd6f9] text-[#1b1b1b]',
  },
  Parcel: {
    symbol: 'Pa',
    label: 'Parcel',
    className: 'tech-symbol--other',
    accent: 'parcel',
    iconSlug: 'parcel',
    fallbackClass: 'bg-[#21374b] text-white',
  },
  Rspack: {
    symbol: 'Rs',
    label: 'Rspack',
    className: 'tech-symbol--other',
    accent: 'rspack',
    iconSlug: 'rspack',
    fallbackClass: 'bg-[#f0db4f] text-[#1b1b1b]',
  },
  Rollup: {
    symbol: 'Ro',
    label: 'Rollup',
    className: 'tech-symbol--other',
    accent: 'rollup',
    iconSlug: 'rollupdotjs',
    fallbackClass: 'bg-[#ec4a3f] text-white',
  },
  esbuild: {
    symbol: 'eb',
    label: 'esbuild',
    className: 'tech-symbol--other',
    accent: 'esbuild',
    iconSlug: 'esbuild',
    fallbackClass: 'bg-[#ffcf00] text-[#1b1b1b]',
  },
  TurboRepo: {
    symbol: 'Tu',
    label: 'TurboRepo',
    className: 'tech-symbol--other',
    accent: 'turbo',
    iconSlug: 'turborepo',
    fallbackClass: 'bg-[#ef4444] text-white',
  },
  Nx: {
    symbol: 'Nx',
    label: 'Nx',
    className: 'tech-symbol--other',
    accent: 'nx',
    iconSlug: 'nx',
    fallbackClass: 'bg-[#143055] text-white',
  },
  Lerna: {
    symbol: 'Le',
    label: 'Lerna',
    className: 'tech-symbol--other',
    accent: 'lerna',
    iconSlug: 'lerna',
    fallbackClass: 'bg-[#9333ea] text-white',
  },
  Rush: {
    symbol: 'Ru',
    label: 'Rush',
    className: 'tech-symbol--other',
    accent: 'rush',
    iconSlug: 'rush',
    fallbackClass: 'bg-[#0078d4] text-white',
  },

  // Testing
  Playwright: {
    symbol: 'Pw',
    label: 'Playwright',
    className: 'tech-symbol--other',
    accent: 'playwright',
    iconSlug: 'playwright',
    fallbackClass: 'bg-[#2ead33] text-white',
  },
  Cypress: {
    symbol: 'Cy',
    label: 'Cypress',
    className: 'tech-symbol--other',
    accent: 'cypress',
    iconSlug: 'cypress',
    fallbackClass: 'bg-[#17202c] text-[#69d3a7]',
  },
  Jest: {
    symbol: 'Je',
    label: 'Jest',
    className: 'tech-symbol--other',
    accent: 'jest',
    iconSlug: 'jest',
    fallbackClass: 'bg-[#c21325] text-white',
  },
  Vitest: {
    symbol: 'Vt',
    label: 'Vitest',
    className: 'tech-symbol--other',
    accent: 'vitest',
    iconSlug: 'vitest',
    fallbackClass: 'bg-[#6e9f18] text-white',
  },
  Mocha: {
    symbol: 'Mo',
    label: 'Mocha',
    className: 'tech-symbol--other',
    accent: 'mocha',
    iconSlug: 'mocha',
    fallbackClass: 'bg-[#8d6748] text-white',
  },
  WebdriverIO: {
    symbol: 'WD',
    label: 'WebdriverIO',
    className: 'tech-symbol--other',
    accent: 'webdriverio',
    iconSlug: 'webdriverio',
    fallbackClass: 'bg-[#ea5906] text-white',
  },
  Karma: {
    symbol: 'Ka',
    label: 'Karma',
    className: 'tech-symbol--other',
    accent: 'karma',
    iconSlug: 'karma',
    fallbackClass: 'bg-[#56c5a0] text-white',
  },
  Istanbul: {
    symbol: 'Is',
    label: 'Istanbul',
    className: 'tech-symbol--other',
    accent: 'istanbul',
    iconSlug: 'jest',
    fallbackClass: 'bg-[#f0db4f] text-[#1b1b1b]',
  },
  c8: {
    symbol: 'c8',
    label: 'c8',
    className: 'tech-symbol--other',
    accent: 'c8',
    iconSlug: 'vitest',
    fallbackClass: 'bg-[#6e9f18] text-white',
  },
  JUnit: {
    symbol: 'JU',
    label: 'JUnit',
    className: 'tech-symbol--other',
    accent: 'junit',
    iconSlug: 'junit5',
    fallbackClass: 'bg-[#25a162] text-white',
  },
  TestNG: {
    symbol: 'TN',
    label: 'TestNG',
    className: 'tech-symbol--other',
    accent: 'testng',
    iconSlug: 'testinglibrary',
    fallbackClass: 'bg-[#e33333] text-white',
  },
  xUnit: {
    symbol: 'xU',
    label: 'xUnit',
    className: 'tech-symbol--other',
    accent: 'xunit',
    iconSlug: 'xunit',
    fallbackClass: 'bg-[#2e9ad0] text-white',
  },
  NUnit: {
    symbol: 'NU',
    label: 'NUnit',
    className: 'tech-symbol--other',
    accent: 'nunit',
    iconSlug: 'nunit',
    fallbackClass: 'bg-[#2d4552] text-white',
  },
  MSTest: {
    symbol: 'MS',
    label: 'MSTest',
    className: 'tech-symbol--other',
    accent: 'mstest',
    iconSlug: 'dotnet',
    fallbackClass: 'bg-[#68217a] text-white',
  },
  PyTest: {
    symbol: 'PT',
    label: 'PyTest',
    className: 'tech-symbol--other',
    accent: 'pytest',
    iconSlug: 'pytest',
    fallbackClass: 'bg-[#0a9edc] text-white',
  },
  'Robot Framework': {
    symbol: 'RF',
    label: 'Robot Framework',
    className: 'tech-symbol--other',
    accent: 'robot',
    iconSlug: 'robotframework',
    fallbackClass: 'bg-[#000000] text-white',
  },
  Selenium: {
    symbol: 'Se',
    label: 'Selenium',
    className: 'tech-symbol--other',
    accent: 'selenium',
    iconSlug: 'selenium',
    fallbackClass: 'bg-[#43b02a] text-white',
  },
  RSpec: {
    symbol: 'RS',
    label: 'RSpec',
    className: 'tech-symbol--other',
    accent: 'rspec',
    iconSlug: 'ruby',
    fallbackClass: 'bg-[#cc342d] text-white',
  },

  // Databases / ORM
  MongoDB: {
    symbol: 'Mg',
    label: 'MongoDB',
    className: 'tech-symbol--other',
    accent: 'mongodb',
    iconSlug: 'mongodb',
    fallbackClass: 'bg-[#47a248] text-white',
  },
  PostgreSQL: {
    symbol: 'Pg',
    label: 'PostgreSQL',
    className: 'tech-symbol--other',
    accent: 'postgresql',
    iconSlug: 'postgresql',
    fallbackClass: 'bg-[#4169e1] text-white',
  },
  MySQL: {
    symbol: 'My',
    label: 'MySQL',
    className: 'tech-symbol--other',
    accent: 'mysql',
    iconSlug: 'mysql',
    fallbackClass: 'bg-[#4479a1] text-white',
  },
  Redis: {
    symbol: 'Re',
    label: 'Redis',
    className: 'tech-symbol--other',
    accent: 'redis',
    iconSlug: 'redis',
    fallbackClass: 'bg-[#dc382d] text-white',
  },
  SQLite: {
    symbol: 'SQ',
    label: 'SQLite',
    className: 'tech-symbol--other',
    accent: 'sqlite',
    iconSlug: 'sqlite',
    fallbackClass: 'bg-[#003b57] text-white',
  },
  Oracle: {
    symbol: 'Or',
    label: 'Oracle',
    className: 'tech-symbol--other',
    accent: 'oracle',
    iconSlug: 'oracle',
    fallbackClass: 'bg-[#f80000] text-white',
  },
  'SQL Server': {
    symbol: 'SS',
    label: 'SQL Server',
    className: 'tech-symbol--other',
    accent: 'sqlserver',
    iconSlug: 'microsoftsqlserver',
    fallbackClass: 'bg-[#cc2927] text-white',
  },
  DynamoDB: {
    symbol: 'Dy',
    label: 'DynamoDB',
    className: 'tech-symbol--other',
    accent: 'dynamodb',
    iconSlug: 'amazondynamodb',
    fallbackClass: 'bg-[#4053d6] text-white',
  },
  Prisma: {
    symbol: 'Pr',
    label: 'Prisma',
    className: 'tech-symbol--other',
    accent: 'prisma',
    iconSlug: 'prisma',
    fallbackClass: 'bg-[#2d3748] text-white',
  },
  TypeORM: {
    symbol: 'TO',
    label: 'TypeORM',
    className: 'tech-symbol--other',
    accent: 'typeorm',
    iconSlug: 'typeorm',
    fallbackClass: 'bg-[#fe0803] text-white',
  },
  Sequelize: {
    symbol: 'Sq',
    label: 'Sequelize',
    className: 'tech-symbol--other',
    accent: 'sequelize',
    iconSlug: 'sequelize',
    fallbackClass: 'bg-[#52b0e7] text-white',
  },
  Hibernate: {
    symbol: 'Hi',
    label: 'Hibernate',
    className: 'tech-symbol--other',
    accent: 'hibernate',
    iconSlug: 'hibernate',
    fallbackClass: 'bg-[#59666c] text-white',
  },
  'Entity Framework': {
    symbol: 'EF',
    label: 'Entity Framework',
    className: 'tech-symbol--other',
    accent: 'ef',
    iconSlug: 'dotnet',
    fallbackClass: 'bg-[#512bd4] text-white',
  },
  SQLAlchemy: {
    symbol: 'SA',
    label: 'SQLAlchemy',
    className: 'tech-symbol--other',
    accent: 'sqlalchemy',
    iconSlug: 'sqlalchemy',
    fallbackClass: 'bg-[#d71f00] text-white',
  },

  // AI
  OpenAI: {
    symbol: 'AI',
    label: 'OpenAI',
    className: 'tech-symbol--other',
    accent: 'openai',
    iconSlug: 'openai',
    fallbackClass: 'bg-[#412991] text-white',
  },
  Anthropic: {
    symbol: 'An',
    label: 'Anthropic',
    className: 'tech-symbol--other',
    accent: 'anthropic',
    iconSlug: 'anthropic',
    fallbackClass: 'bg-[#191919] text-white',
  },
  LangChain: {
    symbol: 'LC',
    label: 'LangChain',
    className: 'tech-symbol--other',
    accent: 'langchain',
    iconSlug: 'langchain',
    fallbackClass: 'bg-[#1c3c3c] text-white',
  },
  LlamaIndex: {
    symbol: 'LI',
    label: 'LlamaIndex',
    className: 'tech-symbol--other',
    accent: 'llamaindex',
    iconSlug: 'meta',
    fallbackClass: 'bg-[#0668e1] text-white',
  },
  HuggingFace: {
    symbol: 'HF',
    label: 'HuggingFace',
    className: 'tech-symbol--other',
    accent: 'huggingface',
    iconSlug: 'huggingface',
    fallbackClass: 'bg-[#ffd21e] text-[#1b1b1b]',
  },
  Ollama: {
    symbol: 'Ol',
    label: 'Ollama',
    className: 'tech-symbol--other',
    accent: 'ollama',
    iconSlug: 'ollama',
    fallbackClass: 'bg-[#000000] text-white',
  },

  // Infra / CI / cloud
  Docker: {
    symbol: 'Do',
    label: 'Docker',
    className: 'tech-symbol--other',
    accent: 'docker',
    iconSlug: 'docker',
    fallbackClass: 'bg-[#2496ed] text-white',
  },
  'Docker Compose': {
    symbol: 'DC',
    label: 'Docker Compose',
    className: 'tech-symbol--other',
    accent: 'docker',
    iconSlug: 'docker',
    fallbackClass: 'bg-[#2496ed] text-white',
  },
  Kubernetes: {
    symbol: 'K8',
    label: 'Kubernetes',
    className: 'tech-symbol--other',
    accent: 'kubernetes',
    iconSlug: 'kubernetes',
    fallbackClass: 'bg-[#326ce5] text-white',
  },
  Terraform: {
    symbol: 'Tf',
    label: 'Terraform',
    className: 'tech-symbol--other',
    accent: 'terraform',
    iconSlug: 'terraform',
    fallbackClass: 'bg-[#844fba] text-white',
  },
  Helm: {
    symbol: 'He',
    label: 'Helm',
    className: 'tech-symbol--other',
    accent: 'helm',
    iconSlug: 'helm',
    fallbackClass: 'bg-[#0f1689] text-white',
  },
  'GitHub Actions': {
    symbol: 'GH',
    label: 'GitHub Actions',
    className: 'tech-symbol--other',
    accent: 'github',
    iconSlug: 'githubactions',
    fallbackClass: 'bg-[#2088ff] text-white',
  },
  'Azure DevOps Pipelines': {
    symbol: 'AD',
    label: 'Azure DevOps Pipelines',
    className: 'tech-symbol--other',
    accent: 'azuredevops',
    iconSlug: 'azuredevops',
    fallbackClass: 'bg-[#0078d4] text-white',
  },
  'GitLab CI': {
    symbol: 'GL',
    label: 'GitLab CI',
    className: 'tech-symbol--other',
    accent: 'gitlab',
    iconSlug: 'gitlab',
    fallbackClass: 'bg-[#fc6d26] text-white',
  },
  Jenkins: {
    symbol: 'Jk',
    label: 'Jenkins',
    className: 'tech-symbol--other',
    accent: 'jenkins',
    iconSlug: 'jenkins',
    fallbackClass: 'bg-[#d24939] text-white',
  },
  Bitrise: {
    symbol: 'Bi',
    label: 'Bitrise',
    className: 'tech-symbol--other',
    accent: 'bitrise',
    iconSlug: 'bitrise',
    fallbackClass: 'bg-[#683d87] text-white',
  },
  Vercel: {
    symbol: 'Ve',
    label: 'Vercel',
    className: 'tech-symbol--other',
    accent: 'vercel',
    iconSlug: 'vercel',
    fallbackClass: 'bg-[#000000] text-white',
  },
  Netlify: {
    symbol: 'Ne',
    label: 'Netlify',
    className: 'tech-symbol--other',
    accent: 'netlify',
    iconSlug: 'netlify',
    fallbackClass: 'bg-[#00c7b7] text-white',
  },
  Firebase: {
    symbol: 'Fb',
    label: 'Firebase',
    className: 'tech-symbol--other',
    accent: 'firebase',
    iconSlug: 'firebase',
    fallbackClass: 'bg-[#dd2c00] text-white',
  },
  'AWS SDK': {
    symbol: 'AW',
    label: 'AWS SDK',
    className: 'tech-symbol--other',
    accent: 'aws',
    iconSlug: 'amazonaws',
    fallbackClass: 'bg-[#ff9900] text-[#1b1b1b]',
  },
  'Azure SDK': {
    symbol: 'Az',
    label: 'Azure SDK',
    className: 'tech-symbol--other',
    accent: 'azure',
    iconSlug: 'microsoftazure',
    fallbackClass: 'bg-[#0078d4] text-white',
  },
  'GCP SDK': {
    symbol: 'GC',
    label: 'GCP SDK',
    className: 'tech-symbol--other',
    accent: 'gcp',
    iconSlug: 'googlecloud',
    fallbackClass: 'bg-[#4285f4] text-white',
  },
  Supabase: {
    symbol: 'Su',
    label: 'Supabase',
    className: 'tech-symbol--other',
    accent: 'supabase',
    iconSlug: 'supabase',
    fallbackClass: 'bg-[#3ecf8e] text-[#1b1b1b]',
  },
  Kafka: {
    symbol: 'Kf',
    label: 'Kafka',
    className: 'tech-symbol--other',
    accent: 'kafka',
    iconSlug: 'apachekafka',
    fallbackClass: 'bg-[#231f20] text-white',
  },
  RabbitMQ: {
    symbol: 'Rb',
    label: 'RabbitMQ',
    className: 'tech-symbol--other',
    accent: 'rabbitmq',
    iconSlug: 'rabbitmq',
    fallbackClass: 'bg-[#ff6600] text-white',
  },
  'AWS SQS': {
    symbol: 'SQ',
    label: 'AWS SQS',
    className: 'tech-symbol--other',
    accent: 'aws',
    iconSlug: 'amazonaws',
    fallbackClass: 'bg-[#ff9900] text-[#1b1b1b]',
  },
  BullMQ: {
    symbol: 'BQ',
    label: 'BullMQ',
    className: 'tech-symbol--other',
    accent: 'bullmq',
    iconSlug: 'redis',
    fallbackClass: 'bg-[#dc382d] text-white',
  },
  NextAuth: {
    symbol: 'NA',
    label: 'NextAuth',
    className: 'tech-symbol--other',
    accent: 'nextauth',
    iconSlug: 'auth0',
    fallbackClass: 'bg-[#000000] text-white',
  },
  Auth0: {
    symbol: 'A0',
    label: 'Auth0',
    className: 'tech-symbol--other',
    accent: 'auth0',
    iconSlug: 'auth0',
    fallbackClass: 'bg-[#eb5424] text-white',
  },
  Clerk: {
    symbol: 'Cl',
    label: 'Clerk',
    className: 'tech-symbol--other',
    accent: 'clerk',
    iconSlug: 'clerk',
    fallbackClass: 'bg-[#6c47ff] text-white',
  },
  Passport: {
    symbol: 'Pa',
    label: 'Passport',
    className: 'tech-symbol--other',
    accent: 'passport',
    iconSlug: 'passport',
    fallbackClass: 'bg-[#34e27a] text-white',
  },
  Sentry: {
    symbol: 'Se',
    label: 'Sentry',
    className: 'tech-symbol--other',
    accent: 'sentry',
    iconSlug: 'sentry',
    fallbackClass: 'bg-[#362d59] text-white',
  },
  OpenTelemetry: {
    symbol: 'OT',
    label: 'OpenTelemetry',
    className: 'tech-symbol--other',
    accent: 'opentelemetry',
    iconSlug: 'opentelemetry',
    fallbackClass: 'bg-[#f5a800] text-[#1b1b1b]',
  },

  Other: {
    symbol: '?',
    label: 'Other',
    className: 'tech-symbol--other',
    accent: 'other',
    fallbackClass:
      'border border-[var(--inv-border)] bg-[var(--inv-surface-muted)] text-[var(--inv-text-muted)]',
  },
};

const DEFAULT_FALLBACK: TechnologySymbolMeta = {
  symbol: '?',
  label: 'Unknown',
  className: 'tech-symbol--other',
  accent: 'other',
  fallbackClass:
    'border border-[var(--inv-border)] bg-[var(--inv-surface-muted)] text-[var(--inv-text-secondary)]',
};

export function getTechnologyIconUrl(iconSlug: string): string {
  return `https://cdn.simpleicons.org/${encodeURIComponent(iconSlug)}`;
}

/** Programming languages (Vue is treated as a framework on frontend cards). */
const PROGRAMMING_LANGUAGES = new Set([
  'JavaScript',
  'TypeScript',
  'C#',
  'Java',
  'Kotlin',
  'Python',
  'Go',
  'Rust',
  'PHP',
  'Ruby',
  'Scala',
  'Swift',
  'SQL',
  '.NET',
  'Dart',
  'C',
  'C++',
  'Objective-C',
  'F#',
  'VB.NET',
  'R',
  'Lua',
  'Perl',
  'Elixir',
  'Clojure',
  'Haskell',
  'Shell',
  'Bash',
]);

const FRONTEND_LANGUAGES = new Set(
  [...PROGRAMMING_LANGUAGES].filter(label => label !== 'Vue'),
);

const FRONTEND_FRAMEWORKS = new Set([
  'React',
  'Next.js',
  'Vue',
  'Nuxt',
  'Angular',
  'Svelte',
  'SolidJS',
  'Astro',
  'Remix',
  'Gatsby',
  'Qwik',
  'React Native',
  'Expo',
  'Ionic',
  'Preact',
  'Ember',
  'Alpine.js',
]);

const BACKEND_LANGUAGES = new Set([...PROGRAMMING_LANGUAGES, 'Node.js']);

/** Prefer real languages over platform/runtime labels on backend cards. */
const BACKEND_PRIMARY_LANGUAGES = new Set(
  [...PROGRAMMING_LANGUAGES].filter(label => label !== '.NET'),
);

const BACKEND_RUNTIME_LABELS = new Set(['Node.js', '.NET']);

const BACKEND_FRAMEWORKS = new Set([
  'Express',
  'NestJS',
  'Fastify',
  'Hono',
  'Koa',
  'Spring Boot',
  'ASP.NET',
  'Flask',
  'Django',
  'FastAPI',
  'Laravel',
  'Gin',
  'Fiber',
  'Echo',
  'Actix',
  'Rocket',
  'Ruby on Rails',
]);

const DATABASES = new Set([
  'MongoDB',
  'PostgreSQL',
  'MySQL',
  'Redis',
  'SQLite',
  'Oracle',
  'SQL Server',
  'DynamoDB',
  'Supabase',
  'Firebase',
]);

export type CardTechnologyKind = 'frontend' | 'backend';

function pickTechnologiesInOrder(
  technologies: string[],
  allowed: Set<string>,
  max: number,
): string[] {
  const result: string[] = [];
  const seen = new Set<string>();

  for (const tech of technologies) {
    const label = tech?.trim();
    if (!label || !allowed.has(label)) continue;

    const key = label.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(label);
    if (result.length >= max) break;
  }

  return result;
}

function mergeTechnologyGroups(...groups: string[][]): string[] {
  const seen = new Set<string>();
  const merged: string[] = [];

  for (const group of groups) {
    for (const label of group) {
      const key = label.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      merged.push(label);
    }
  }

  return merged;
}

function pickBackendLanguageForCard(technologies: string[]): string[] {
  const primary = pickTechnologiesInOrder(
    technologies,
    BACKEND_PRIMARY_LANGUAGES,
    1,
  );
  if (primary.length > 0) {
    return primary;
  }
  return pickTechnologiesInOrder(technologies, BACKEND_RUNTIME_LABELS, 1);
}

/** Card footer: frontend → language + framework; backend → frontend framework + backend language + database. */
export function filterCardTechnologies(
  technologies: string[],
  kind: CardTechnologyKind,
): string[] {
  if (kind === 'frontend') {
    return mergeTechnologyGroups(
      pickTechnologiesInOrder(technologies, FRONTEND_LANGUAGES, 1),
      pickTechnologiesInOrder(technologies, FRONTEND_FRAMEWORKS, 1),
    );
  }

  return mergeTechnologyGroups(
    pickTechnologiesInOrder(technologies, FRONTEND_FRAMEWORKS, 1),
    pickBackendLanguageForCard(technologies),
    pickTechnologiesInOrder(technologies, DATABASES, 1),
  );
}

export function filterProgrammingLanguages(technologies: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];

  for (const tech of technologies) {
    const label = tech?.trim();
    if (!label || !PROGRAMMING_LANGUAGES.has(label)) continue;

    const key = label.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(label);
  }

  return result;
}

export function getTechnologySymbolMeta(
  technology: string,
): TechnologySymbolMeta {
  const mapped = TECHNOLOGY_MAP[technology];
  if (mapped) {
    return mapped;
  }

  const initials = technology
    .replace(/[^a-zA-Z0-9+#.]/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .map(part => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return {
    ...DEFAULT_FALLBACK,
    symbol: initials || technology.slice(0, 2).toUpperCase() || '?',
      label: technology,
  };
}

export function getPrimaryTechnologyAccent(
  technologies: string[],
): string {
  if (!technologies.length) return 'default';
  return getTechnologySymbolMeta(technologies[0]).accent;
}

export function formatTechnologySymbols(technologies: string[]): string {
  return technologies
    .map(tech => getTechnologySymbolMeta(tech).symbol)
    .join(' · ');
}

export function formatTechnologyLabels(technologies: string[]): string {
  return technologies
    .map(tech => getTechnologySymbolMeta(tech).label)
    .join(' · ');
}

export function buildRingText(technologies: string[]): string {
  const labels = technologies
    .map(tech => getTechnologySymbolMeta(tech).label.toUpperCase())
    .join(' • ');

  if (labels.length <= 18) {
    return `${labels} • ${labels}`;
  }

  return labels;
}

export function componentInitials(name: string): string {
  const parts = name
    .replace(/[^a-zA-Z0-9\s-_]/g, ' ')
    .split(/[\s-_]+/)
    .filter(Boolean);

  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }

  return (parts[0]?.slice(0, 2) ?? 'CO').toUpperCase();
}
